const SUBSCRIPTION_TITLE = 'Subscribe & Save (10% off on your 1st order)';
const FREQUENCY_LABEL = 'Deliver every:';
const PURCHASE_OPTION_LEGEND = 'Purchase option';
const SUBSCRIPTION_BENEFITS = [
  'Save 10% on every product in your subscription',
  'Skip or cancel anytime',
  'Email reminder before your order ships',
];

class SubscriptionsWidget {

  constructor() {
    this.initialized = false;
    this.globalListenersBound = false;
    this.onVariantInputChange = this.onVariantInputChange.bind(this);
    this.onVariantButtonClick = this.onVariantButtonClick.bind(this);
    this.init();
  }

  init() {
    this.bindGlobalListeners();

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    const subscriptionBlocks = document.querySelectorAll('.shopify_subscriptions_app_block');

    subscriptionBlocks.forEach((block) => {
      this.transformSubscriptionBlock(block);
      block.setAttribute('data-subscription-transformed', 'true');
    });
  }

  transformSubscriptionBlock(block) {
    const fieldset = block.querySelector('.shopify_subscriptions_fieldset');
    if (!fieldset) {
      console.warn('No se encontro .shopify_subscriptions_fieldset en el bloque');
      return;
    }

    const subscriptionContainer = block.closest('.shopify_subscriptions_app_container');
    const sectionId = subscriptionContainer ? subscriptionContainer.getAttribute('data-section-id') : null;

    let productForm = null;
    if (sectionId) {
      const formIdPattern = `product-form-${sectionId}`;
      productForm = document.getElementById(formIdPattern);
    }

    if (!productForm) {
      const variantId = block.getAttribute('data-variant-id');
      if (variantId) {
        const variantInput = document.querySelector(`form[id^="product-form-"] input[name="id"][value="${variantId}"]`);
        productForm = variantInput?.form || null;
      }
    }

    if (!productForm) {
      productForm = document.querySelector('form[id^="product-form-"]');
    }

    const formId = productForm ? productForm.getAttribute('id') : null;
    const planCard = fieldset.querySelector('.plan_card');
    const groups = planCard ? Array.from(planCard.querySelectorAll('.subscription_group')) : [];
    const oneTimePurchaseWrapper = groups.find((group) => group.querySelector('[data-radio-type="one_time_purchase"]'));
    const subscribeWrapper = groups.find((group) => group.querySelector('.group_list'));

    if (!subscribeWrapper || !oneTimePurchaseWrapper) {
      return;
    }

    this.ensureLegend(fieldset);

    block.classList.add('subscriptions-widget-ready');
    oneTimePurchaseWrapper.classList.add('subscription-option', 'subscription-option--one-time');
    subscribeWrapper.classList.add('subscription-option', 'subscription-option--subscription');

    this.removeOneTimeCustomRadioClass(oneTimePurchaseWrapper);

    const allRadios = fieldset.querySelectorAll('input[type="radio"]');

    allRadios.forEach((radio) => {
      const sellingPlanId = radio.getAttribute('data-selling-plan-id');

      if (sellingPlanId) {
        radio.name = 'selling_plan';
        radio.value = sellingPlanId;
      }

      if (formId && typeof formId === 'string' && !radio.hasAttribute('form')) {
        radio.setAttribute('form', formId);
      }
    });

    this.addSubscribeRadioButton(subscribeWrapper, fieldset, oneTimePurchaseWrapper, formId);
    this.upsertSubscriptionHeader(subscribeWrapper);

    const frequencyList = subscribeWrapper.querySelector('.group_list');
    if (frequencyList) {
      this.createFrequencyDropdown(frequencyList, subscribeWrapper);
    }
    this.upsertSubscriptionBenefitsList(subscribeWrapper);

    this.handleRadioChanges(fieldset, subscribeWrapper, oneTimePurchaseWrapper);
    this.syncSubscriptionState(subscribeWrapper, oneTimePurchaseWrapper);
    this.syncOneTimePurchasePrice(block, { sectionId });
  }

  // #518: the app's fieldset ships without a legend, so the two purchase-option radios have no group name.
  ensureLegend(fieldset) {
    if (fieldset.querySelector(':scope > legend')) return;

    const legend = document.createElement('legend');
    legend.className = 'visually-hidden';
    legend.textContent = PURCHASE_OPTION_LEGEND;
    fieldset.prepend(legend);
  }

  bindGlobalListeners() {
    if (this.globalListenersBound) return;

    this.globalListenersBound = true;

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const eventData = event?.data || {};
        this.syncOneTimePriceBySection(eventData.sectionId, eventData.variant);
      });
    }

    document.addEventListener('change', this.onVariantInputChange, true);
    document.addEventListener('click', this.onVariantButtonClick, true);
  }

  onVariantInputChange(event) {
    const input = event?.target;
    if (!(input instanceof HTMLInputElement)) return;
    if (input.name !== 'id') return;

    const form = input.closest('form[id^="product-form-"]');
    const sectionId = this.extractSectionIdFromFormId(form?.id);

    setTimeout(() => {
      this.syncOneTimePriceBySection(sectionId);
    }, 0);
  }

  onVariantButtonClick(event) {
    const clickedButton = event?.target?.closest?.('.quantity-selector-buttons .quantity-btn');
    if (!clickedButton) return;

    const productInfo = clickedButton.closest('product-info');
    const sectionId = productInfo?.dataset?.section || null;

    setTimeout(() => {
      this.syncOneTimePriceBySection(sectionId);
    }, 0);
  }

  extractSectionIdFromFormId(formId) {
    if (!formId || typeof formId !== 'string') return null;

    if (formId.startsWith('product-form-installment-')) {
      return formId.replace('product-form-installment-', '');
    }

    if (formId.startsWith('product-form-')) {
      return formId.replace('product-form-', '');
    }

    return null;
  }

  getBlockSectionId(block) {
    const subscriptionContainer = block.closest('.shopify_subscriptions_app_container');
    return subscriptionContainer?.getAttribute('data-section-id') || null;
  }

  syncOneTimePriceBySection(sectionId, variant = null) {
    const subscriptionBlocks = document.querySelectorAll('.shopify_subscriptions_app_block');

    subscriptionBlocks.forEach((block) => {
      const blockSectionId = this.getBlockSectionId(block);

      if (sectionId && blockSectionId && blockSectionId !== sectionId) {
        return;
      }

      this.syncOneTimePurchasePrice(block, { sectionId: blockSectionId || sectionId, variant });
    });
  }

  syncOneTimePurchasePrice(block, context = {}) {
    const fieldset = block.querySelector('.shopify_subscriptions_fieldset');
    if (!fieldset) return;

    const planCard = fieldset.querySelector('.plan_card');
    if (!planCard) return;

    const groups = Array.from(planCard.querySelectorAll('.subscription_group'));
    const oneTimePurchaseWrapper = groups.find((group) => group.querySelector('[data-radio-type="one_time_purchase"]'));

    if (!oneTimePurchaseWrapper) return;

    let priceTargets = Array.from(
      oneTimePurchaseWrapper.querySelectorAll('.in_widget_price, .shopify_subscriptions_in_widget_price')
    );

    if (priceTargets.length === 0) {
      const fallbackTarget = oneTimePurchaseWrapper.querySelector(
        '.title_and_price_wrapper > span:last-child, .title_and_price_wrapper > div:last-child'
      );

      if (fallbackTarget && !fallbackTarget.classList?.contains('group_name')) {
        priceTargets = [fallbackTarget];
      }
    }

    if (priceTargets.length === 0) return;

    const referenceText = priceTargets[0].textContent.trim();
    const nextPriceText = this.resolveOneTimePriceText(context, referenceText);

    if (nextPriceText) {
      priceTargets.forEach((target) => {
        if (target.textContent.trim() !== nextPriceText) {
          target.textContent = nextPriceText;
        }
      });
    }

    this.syncOneTimeAccessibleName(oneTimePurchaseWrapper, nextPriceText || referenceText);
  }

  // #541: the app bakes the variant price into the radio's aria-label once and never refreshes it, so after
  // picking the 6-pack the spoken name said $4.29 while the visible label said $24.48.
  syncOneTimeAccessibleName(oneTimePurchaseWrapper, priceText) {
    const radio = oneTimePurchaseWrapper.querySelector('input[type="radio"][data-radio-type="one_time_purchase"]');
    const labelText = radio?.parentElement?.querySelector('span')?.textContent?.trim();
    if (!radio || !labelText) return;

    radio.setAttribute('aria-label', `${labelText} ${priceText}`.trim());
  }

  resolveOneTimePriceText(context, referenceText) {
    const sectionId = context?.sectionId || null;

    const activeVariantButtonPrice = this.getActiveVariantButtonPriceText(sectionId);
    if (activeVariantButtonPrice) {
      return this.normalizePriceForDisplay(activeVariantButtonPrice, referenceText);
    }

    const variantPrice = context?.variant?.price;
    if (typeof variantPrice === 'number') {
      const formattedVariantPrice = this.formatVariantPrice(variantPrice, referenceText);
      return this.normalizePriceForDisplay(formattedVariantPrice, referenceText);
    }

    const productPrice = this.extractPriceTextFromMainPrice(sectionId);
    if (productPrice) {
      return this.normalizePriceForDisplay(productPrice, referenceText);
    }

    return null;
  }

  getActiveVariantButtonPriceText(sectionId) {
    const scopedRoot = sectionId
      ? document.querySelector(`product-info[data-section="${sectionId}"]`) || document
      : document;

    const activeButton = scopedRoot.querySelector('.quantity-selector-buttons .quantity-btn.active');
    if (!activeButton) return null;

    let priceText = activeButton.getAttribute('data-price')?.trim() || '';

    if (!priceText) {
      const priceNode = activeButton.querySelector('.quantity-btn__price');
      if (priceNode) {
        const clone = priceNode.cloneNode(true);
        clone.querySelectorAll('s, .quantity-btn__compare').forEach((node) => node.remove());
        priceText = clone.textContent.trim();
      }
    }

    return priceText || null;
  }

  extractPriceTextFromMainPrice(sectionId) {
    const scopedRoot = sectionId
      ? document.querySelector(`product-info[data-section="${sectionId}"]`) || document
      : document;

    const priceContainer = sectionId
      ? scopedRoot.querySelector(`#price-${sectionId}`) || document.getElementById(`price-${sectionId}`)
      : scopedRoot.querySelector('[id^="price-"]');

    if (!priceContainer) return null;

    const priceSelectors = [
      '.price-item--sale',
      '.price__sale .price-item--last',
      '.price__regular .price-item--regular',
      '.price-item--regular',
    ];

    for (const selector of priceSelectors) {
      const priceNode = priceContainer.querySelector(selector);
      const priceText = priceNode?.textContent?.trim();

      if (priceText) {
        return priceText;
      }
    }

    return null;
  }

  formatVariantPrice(priceInCents, referenceText) {
    if (typeof priceInCents !== 'number') return null;

    const currencyCodeFromText = referenceText?.match(/\b[A-Z]{3}\b/)?.[0];
    const currencyCode = window?.Shopify?.currency?.active || currencyCodeFromText || 'USD';

    let formattedPrice = null;

    try {
      formattedPrice = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 2,
      }).format(priceInCents / 100);
    } catch (error) {
      formattedPrice = `$${(priceInCents / 100).toFixed(2)}`;
    }

    return formattedPrice;
  }

  normalizePriceForDisplay(priceText, referenceText) {
    if (!priceText) return null;

    const normalizedPrice = priceText.replace(/\s+/g, ' ').trim();
    const currencyCodeFromText = referenceText?.match(/\b[A-Z]{3}\b/)?.[0];

    if (
      currencyCodeFromText &&
      !new RegExp(`\\b${currencyCodeFromText}\\b`).test(normalizedPrice)
    ) {
      return `${normalizedPrice} ${currencyCodeFromText}`;
    }

    return normalizedPrice;
  }

  removeOneTimeCustomRadioClass(oneTimePurchaseWrapper) {
    if (!oneTimePurchaseWrapper) return;

    const oneTimeRadio = oneTimePurchaseWrapper.querySelector('input[type="radio"][data-radio-type="one_time_purchase"]');
    oneTimeRadio?.classList.remove('custom_radio_button');

    const customRadioElements = oneTimePurchaseWrapper.querySelectorAll('.custom_radio_button');
    customRadioElements.forEach((element) => {
      element.classList.remove('custom_radio_button');
    });
  }

  addSubscribeRadioButton(subscribeWrapper, fieldset, oneTimePurchaseWrapper, formId) {
    const purchaseOptionWrapper = subscribeWrapper.querySelector(':scope > .title_and_price_wrapper');
    if (!purchaseOptionWrapper) return;

    let mainRadio = purchaseOptionWrapper.querySelector('input[type="radio"][data-main-subscription]');

    if (!mainRadio) {
      const groupName = purchaseOptionWrapper.querySelector('.group_name');

      mainRadio = document.createElement('input');
      mainRadio.type = 'radio';
      mainRadio.name = this.getRadioGroupName(fieldset);
      mainRadio.setAttribute('data-main-subscription', 'true');
      mainRadio.value = 'subscription';
      mainRadio.className = 'subscription-group-check';

      if (formId) {
        mainRadio.setAttribute('form', formId);
      }

      if (groupName) {
        // #541: name the radio from its visible title so the spoken name contains the visible label.
        this.subscriptionTitleCount = (this.subscriptionTitleCount || 0) + 1;
        groupName.id = groupName.id || `subscription-title-${this.subscriptionTitleCount}`;
        mainRadio.setAttribute('aria-labelledby', groupName.id);
        groupName.before(mainRadio);
      } else {
        mainRadio.setAttribute('aria-label', SUBSCRIPTION_TITLE);
        purchaseOptionWrapper.prepend(mainRadio);
      }
    }

    if (mainRadio.dataset.bound === 'true') {
      return;
    }

    mainRadio.dataset.bound = 'true';

    mainRadio.addEventListener('change', () => {
      if (!mainRadio.checked) {
        return;
      }

      const oneTimeRadio = oneTimePurchaseWrapper?.querySelector('input[type="radio"][data-radio-type="one_time_purchase"]');
      if (oneTimeRadio) {
        oneTimeRadio.checked = false;
      }

      const select = subscribeWrapper.querySelector('select.frequency-dropdown');
      const frequencyRadios = Array.from(
        subscribeWrapper.querySelectorAll('.group_list input[type="radio"][data-selling-plan-id]')
      );
      const selectedRadio =
        frequencyRadios.find((radio) => radio.checked) ||
        (select
          ? frequencyRadios.find((radio) => radio.id === select.options[select.selectedIndex]?.getAttribute('data-radio-id'))
          : null) ||
        frequencyRadios[0];

      if (selectedRadio) {
        selectedRadio.checked = true;
        selectedRadio.dispatchEvent(new Event('change', { bubbles: true }));
      }

      this.syncSubscriptionState(subscribeWrapper, oneTimePurchaseWrapper);
    });
  }

  createFrequencyDropdown(frequencyList, subscribeWrapper) {
    let dropdownWrapper = subscribeWrapper.querySelector('.frequency-dropdown-wrapper');
    let select = subscribeWrapper.querySelector('select.frequency-dropdown');

    const radioButtons = frequencyList.querySelectorAll('input[type="radio"][data-selling-plan-id]');
    if (radioButtons.length === 0) return;

    if (!dropdownWrapper) {
      dropdownWrapper = document.createElement('div');
      dropdownWrapper.className = 'frequency-dropdown-wrapper';

      const label = document.createElement('span');
      label.className = 'frequency-dropdown-label';
      label.textContent = FREQUENCY_LABEL;
      this.frequencyLabelCount = (this.frequencyLabelCount || 0) + 1;
      label.id = `deliveryFrequencyLabel-${this.frequencyLabelCount}`;

      select = document.createElement('select');
      select.className = 'frequency-dropdown';
      select.setAttribute('id', 'deliveryFrequency');
      select.setAttribute('name', 'delivery-frequency');
      select.setAttribute('aria-labelledby', label.id);

      dropdownWrapper.appendChild(label);
      dropdownWrapper.appendChild(select);
      frequencyList.before(dropdownWrapper);
    }

    if (!select) return;

    select.innerHTML = '';

    radioButtons.forEach((radio, index) => {
      if (!radio.id) {
        radio.id = `subscription-radio-${Date.now()}-${index}`;
      }

      const option = document.createElement('option');
      option.value = radio.getAttribute('data-selling-plan-id') || `${index}`;
      option.setAttribute('data-radio-id', radio.id);
      option.textContent = this.getFrequencyLabel(radio, index);

      if (radio.checked) {
        option.selected = true;
      }

      select.appendChild(option);
    });

    if (select.dataset.bound === 'true') {
      return;
    }

    select.dataset.bound = 'true';

    select.addEventListener('change', (e) => {
      const selectedOption = e.target.options[e.target.selectedIndex];
      const radioId = selectedOption.getAttribute('data-radio-id');
      const radio = radioId ? document.getElementById(radioId) : null;

      if (radio) {
        radio.checked = true;

        const mainRadio = subscribeWrapper.querySelector('input[data-main-subscription]');
        if (mainRadio) {
          mainRadio.checked = true;
        }

        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  upsertSubscriptionHeader(subscribeWrapper) {
    const purchaseOptionWrapper = subscribeWrapper.querySelector(':scope > .title_and_price_wrapper');
    if (!purchaseOptionWrapper) return;

    const groupName = purchaseOptionWrapper.querySelector('.group_name');
    if (!groupName) return;

    if (!groupName.dataset.originalText) {
      groupName.dataset.originalText = groupName.textContent.trim();
    }

    groupName.textContent = SUBSCRIPTION_TITLE;
  }

  upsertSubscriptionBenefitsList(subscribeWrapper) {
    const anchor =
      subscribeWrapper.querySelector('.frequency-dropdown-wrapper') ||
      subscribeWrapper.querySelector('.group_list') ||
      subscribeWrapper;

    let list = subscribeWrapper.querySelector('.subscription-benefits-list');

    if (!list) {
      list = document.createElement('ul');
      list.className = 'subscription-benefits-list';
      anchor.insertAdjacentElement('afterend', list);
    }

    list.innerHTML = '';

    SUBSCRIPTION_BENEFITS.forEach((benefit) => {
      const item = document.createElement('li');
      item.className = 'subscription-benefits-list__item';

      const icon = document.createElement('span');
      icon.className = 'subscription-benefits-list__icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.textContent = '\u2713';

      const text = document.createElement('span');
      text.className = 'subscription-benefits-list__text';
      text.textContent = benefit;

      item.appendChild(icon);
      item.appendChild(text);
      list.appendChild(item);
    });
  }

  handleRadioChanges(fieldset, subscribeWrapper, oneTimePurchaseWrapper) {
    if (oneTimePurchaseWrapper) {
      const oneTimeRadio = oneTimePurchaseWrapper.querySelector('input[type="radio"][data-radio-type="one_time_purchase"]');
      if (oneTimeRadio && oneTimeRadio.dataset.bound !== 'true') {
        oneTimeRadio.dataset.bound = 'true';

        oneTimeRadio.addEventListener('change', () => {
          if (!oneTimeRadio.checked) {
            return;
          }

          const mainRadio = subscribeWrapper.querySelector('input[data-main-subscription]');
          if (mainRadio) {
            mainRadio.checked = false;
          }

          const frequencyRadios = subscribeWrapper.querySelectorAll('.group_list input[type="radio"][data-selling-plan-id]');
          frequencyRadios.forEach((radio) => {
            radio.checked = false;
          });

          this.syncSubscriptionState(subscribeWrapper, oneTimePurchaseWrapper);
        });
      }
    }

    const frequencyRadios = fieldset.querySelectorAll('.group_list input[type="radio"][data-selling-plan-id]');
    frequencyRadios.forEach((radio) => {
      if (radio.dataset.bound === 'true') {
        return;
      }

      radio.dataset.bound = 'true';

      radio.addEventListener('change', (e) => {
        if (e.target.checked) {
          const select = subscribeWrapper.querySelector('select.frequency-dropdown');
          if (select) {
            const option = Array.from(select.options).find((opt) => opt.getAttribute('data-radio-id') === e.target.id);
            if (option) {
              select.value = option.value;
            }
          }

          const mainRadio = subscribeWrapper.querySelector('input[data-main-subscription]');
          if (mainRadio) {
            mainRadio.checked = true;
          }

          if (oneTimePurchaseWrapper) {
            const oneTimeRadio = oneTimePurchaseWrapper.querySelector('input[type="radio"][data-radio-type="one_time_purchase"]');
            if (oneTimeRadio) {
              oneTimeRadio.checked = false;
            }
          }
        }

        this.syncSubscriptionState(subscribeWrapper, oneTimePurchaseWrapper);
      });
    });
  }

  getRadioGroupName(fieldset) {
    const existingRadio = fieldset.querySelector('input[data-radio-type="one_time_purchase"]');
    // #518: one name makes "One-time purchase" and "Subscribe & Save" a single native radio group.
    return existingRadio ? existingRadio.name : 'purchaseOption';
  }

  getFrequencyLabel(radio, index) {
    const labelText = radio.closest('.group_list_item')?.querySelector('.title_and_price_wrapper > span')?.textContent?.trim();
    return labelText || `Option ${index + 1}`;
  }

  syncSubscriptionState(subscribeWrapper, oneTimePurchaseWrapper) {
    const mainRadio = subscribeWrapper.querySelector('input[data-main-subscription]');
    const frequencyRadios = Array.from(
      subscribeWrapper.querySelectorAll('.group_list input[type="radio"][data-selling-plan-id]')
    );
    const activeFrequency = frequencyRadios.find((radio) => radio.checked);
    const isSubscriptionSelected = Boolean(activeFrequency);

    subscribeWrapper.classList.toggle('subscription-option--selected', isSubscriptionSelected);
    subscribeWrapper.classList.toggle('subscription-option--expanded', isSubscriptionSelected);

    if (mainRadio) {
      mainRadio.checked = isSubscriptionSelected;
    }

    if (oneTimePurchaseWrapper) {
      const oneTimeRadio = oneTimePurchaseWrapper.querySelector('input[type="radio"][data-radio-type="one_time_purchase"]');
      oneTimePurchaseWrapper.classList.toggle('subscription-option--selected', Boolean(oneTimeRadio?.checked));
    }

    const policy = subscribeWrapper.closest('.shopify_subscriptions_fieldset')?.querySelector('.shopify_subscriptions_app_policy');
    if (policy) {
      policy.style.visibility = isSubscriptionSelected ? 'visible' : 'hidden';
    }
  }
}

let widgetInstance = null;

function initWidget() {
  if (!widgetInstance) {
    widgetInstance = new SubscriptionsWidget();
  } else {
    widgetInstance.setup();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initWidget);
} else {
  initWidget();
}

document.addEventListener('variant:change', initWidget);

const observer = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    if (mutation.addedNodes.length) {
      mutation.addedNodes.forEach((node) => {
        if (
          node.nodeType === 1 &&
          (node.classList?.contains('shopify_subscriptions_app_block') || node.querySelector?.('.shopify_subscriptions_app_block'))
        ) {
          setTimeout(initWidget, 100);
        }
      });
    }
  });
});

if (document.body) {
  observer.observe(document.body, { childList: true, subtree: true });
}
